// Unified Positions Panel v2 — renders from the structured
// GET /api/positions endpoint (sim from sim_positions + SIM_BRACKET_MAP,
// live from the broker account snapshot). No more regex-scraping of
// eval-ring notes client-side. Per-row sim Close reuses the engine's
// sim-close machinery (journal manual_close row, bracket-map removal,
// eval-flat, SimAdapter exit) so the audit trail stays sim-matches-live.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { positions as positionsApi } from "../api/endpoints";
import type { PositionRow } from "../api/endpoints";
import { usd } from "../lib/format";

/** "in 22m" / "23m late" from the endpoint's minutes_until. */
function etaLabel(m: number): string {
  const abs = Math.abs(m);
  const span =
    abs >= 60 ? `${Math.floor(abs / 60)}h${abs % 60 ? ` ${abs % 60}m` : ""}` : `${abs}m`;
  return m >= 0 ? `in ${span}` : `${span} late`;
}

const NO_STOP_TITLE =
  "No stop level known — bracket map cold (engine restart?) and no stop in the entry eval. This is an alarm state, not a display quirk.";

// ── Sim close button (two-click confirm, per-row local state) ───────────────

function CloseSimButton({ row }: { row: PositionRow }) {
  const qc = useQueryClient();
  const [armed, setArmed] = useState(false);

  const closeQ = useMutation({
    mutationFn: () => positionsApi.closeSim(row.algo_id!, row.account_id),
    onSuccess: () => {
      setArmed(false);
      // Refresh everything the close touches: positions, accounts (UPL),
      // algos (eval ring → sentiment board).
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["algos"] });
    },
    onError: () => setArmed(false),
  });

  if (!armed) {
    return (
      <button
        className="btn btn-xs"
        disabled={closeQ.isPending}
        title="Close this sim position at market (two-click confirm)"
        onClick={() => setArmed(true)}
      >
        ✕
      </button>
    );
  }
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      <button
        className="btn btn-xs"
        style={{ color: "var(--bad)", borderColor: "rgba(255, 82, 82, 0.3)" }}
        disabled={closeQ.isPending}
        title={
          `Close ${row.sleeve} at market` +
          (row.mark != null ? ` ~${row.mark.toFixed(2)}, est ${usd(row.upl, { sign: true })}` : "") +
          " — journals manual_close. Runner may re-enter on its next signal."
        }
        onClick={() => closeQ.mutate()}
      >
        {closeQ.isPending ? "…" : "confirm"}
      </button>
      <button className="btn btn-xs" disabled={closeQ.isPending} onClick={() => setArmed(false)}>
        keep
      </button>
    </span>
  );
}

// ── Live close button (pause-aware, two-click confirm) ─────────────────────
// Closing a LIVE position engages a sleeve pause so the runner can't
// re-enter on its next signal. The pause is VISIBLE state — the row
// renders a "paused" pill with a resume button until the operator
// clears it. This is deliberate: a close that silently re-arms itself
// is hidden state; a pause you must explicitly lift is not.

function CloseLiveButton({ row }: { row: PositionRow }) {
  const qc = useQueryClient();
  const [armed, setArmed] = useState(false);

  const resumeQ = useMutation({
    mutationFn: () => positionsApi.resume(row.algo_id!, row.account_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["algos"] });
    },
  });

  const closeQ = useMutation({
    mutationFn: () =>
      positionsApi.closeLive(row.algo_id!, row.account_id, row.symbol),
    onSuccess: () => {
      setArmed(false);
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["algos"] });
    },
    onError: () => setArmed(false),
  });

  // Paused sleeve → resume affordance replaces the close button.
  if (row.paused) {
    return (
      <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
        <span className="pill warn" style={{ fontSize: 9 }} title="Dispatch blocked — the sleeve cannot re-enter until you resume it">
          paused
        </span>
        <button
          className="btn btn-xs"
          disabled={resumeQ.isPending}
          title="Resume dispatch for this sleeve (it may enter on its next signal)"
          onClick={() => resumeQ.mutate()}
        >
          {resumeQ.isPending ? "…" : "resume"}
        </button>
      </span>
    );
  }

  if (!armed) {
    return (
      <button
        className="btn btn-xs"
        disabled={closeQ.isPending}
        title="Close this LIVE position at market (two-click confirm). The sleeve pauses — resume to allow re-entry."
        onClick={() => setArmed(true)}
      >
        ✕
      </button>
    );
  }
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      <button
        className="btn btn-xs"
        style={{ color: "var(--bad)", borderColor: "rgba(255, 82, 82, 0.3)" }}
        disabled={closeQ.isPending}
        title={
          `CLOSE LIVE ${row.symbol} at market` +
          (row.upl != null ? `, est ${usd(row.upl, { sign: true })}` : "") +
          ` — cancels working orders, fires exit, then PAUSES sleeve ${row.sleeve}. Resume to re-enable.`
        }
        onClick={() => closeQ.mutate()}
      >
        {closeQ.isPending ? "…" : "close live"}
      </button>
      <button className="btn btn-xs" disabled={closeQ.isPending} onClick={() => setArmed(false)}>
        keep
      </button>
    </span>
  );
}

// ── Shared cells ────────────────────────────────────────────────────────────

function StopRiskCell({ r }: { r: PositionRow }) {
  if (r.stop == null) {
    return (
      <span className="pill warn" style={{ fontSize: 9 }} title={NO_STOP_TITLE}>
        ⚠ no SL
      </span>
    );
  }
  return (
    <span title={r.stop_trailing ? "trailing stop" : "fixed stop"}>
      {r.stop.toFixed(2)}
      {r.stop_trailing && <span className="muted"> ↝</span>}
      {" → "}
      <span className="bad">{usd(r.risk_usd)}</span>
    </span>
  );
}

function ExitCell({ r }: { r: PositionRow }) {
  return (
    <td className="num muted" title={r.exit_eta ? `scheduled exit ${r.exit_eta.et_time}` : undefined}>
      {r.exit_eta ? etaLabel(r.exit_eta.minutes_until) : "—"}
    </td>
  );
}

function TotalsLine({ upl, risk }: { upl: number; risk: number }) {
  return (
    <div className="muted small" style={{ marginTop: 4 }}>
      open UPL{" "}
      <span className={upl >= 0 ? "desk-ticker-ok" : "desk-ticker-bad"}>
        {usd(upl, { sign: true })}
      </span>
      {risk > 0 && (
        <>
          {" · "}open risk <span className="bad">{usd(risk)}</span>
        </>
      )}
    </div>
  );
}

// ── Sub-tables ──────────────────────────────────────────────────────────────

function SimTable({ rows }: { rows: PositionRow[] }) {
  const totals = {
    upl: rows.reduce((a, r) => a + (r.upl ?? 0), 0),
    risk: rows.reduce((a, r) => a + (r.risk_usd ?? 0), 0),
  };
  return (
    <>
      <table>
        <thead>
          <tr>
            <th>Sleeve</th>
            <th className="num">Side</th>
            <th className="num">Qty</th>
            <th className="num">Entry</th>
            <th className="num">Mark</th>
            <th className="num">UPL</th>
            <th className="num">Stop → Risk$</th>
            <th className="num">Exit</th>
            <th aria-label="close" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.algo_id}:${r.account_id}`}>
              <td className="small" title={`${r.account_name} · ${r.symbol}`}>
                {r.sleeve}
              </td>
              <td className={"num " + (r.side === "short" ? "bad" : "")}>{r.side}</td>
              <td className="num">{r.qty}</td>
              <td className="num">{r.entry.toFixed(2)}</td>
              <td className="num" title={r.mark_ts ? `bar ${r.mark_ts.slice(11, 16)} UTC` : undefined}>
                {r.mark != null ? r.mark.toFixed(2) : <span className="muted">—</span>}
              </td>
              <td
                className={
                  "num " +
                  (r.upl != null ? (r.upl >= 0 ? "desk-ticker-ok" : "desk-ticker-bad") : "muted")
                }
              >
                {usd(r.upl, { sign: true })}
              </td>
              <td className="num">
                <StopRiskCell r={r} />
              </td>
              <ExitCell r={r} />
              <td>
                <CloseSimButton row={r} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <TotalsLine upl={totals.upl} risk={totals.risk} />
    </>
  );
}

function LiveTable({ rows }: { rows: PositionRow[] }) {
  const totals = {
    upl: rows.reduce((a, r) => a + (r.upl ?? 0), 0),
    risk: rows.reduce((a, r) => a + (r.risk_usd ?? 0), 0),
  };
  return (
    <>
      <table>
        <thead>
          <tr>
            <th>Symbol</th>
            <th className="num">Side</th>
            <th className="num">Qty</th>
            <th className="num">Entry</th>
            <th className="num">UPL</th>
            <th className="num">Stop → Risk$</th>
            <th className="num">Exit</th>
            <th aria-label="close" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.algo_id}:${r.account_id}:${r.symbol}`} style={r.paused ? { opacity: 0.65 } : undefined}>
              <td>
                <span className="mono">{r.symbol}</span>
                <span className="muted small" style={{ marginLeft: 4 }}>
                  {r.sleeve}
                </span>
              </td>
              <td className="num">
                <span
                  className={"pill " + (r.side === "long" ? "good" : "bad")}
                  style={{ fontSize: 9 }}
                >
                  {r.side === "long" ? "LONG" : "SHORT"}
                </span>
              </td>
              <td className="num">{r.qty}</td>
              <td className="num">{r.entry.toFixed(2)}</td>
              <td
                className={
                  "num " + (r.upl != null && r.upl >= 0 ? "desk-ticker-ok" : "desk-ticker-bad")
                }
              >
                {usd(r.upl, { sign: true })}
              </td>
              <td className="num">
                <StopRiskCell r={r} />
              </td>
              <ExitCell r={r} />
              <td>
                <CloseLiveButton row={r} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <TotalsLine upl={totals.upl} risk={totals.risk} />
    </>
  );
}

// ── Component ──────────────────────────────────────────────────────────────

export function PositionsPanel() {
  // Structured positions — the panel's single source.
  const positionsQ = useQuery({
    queryKey: ["positions"],
    queryFn: positionsApi.list,
    refetchInterval: 2500,
  });

  const sim = positionsQ.data?.sim ?? [];
  const live = positionsQ.data?.live ?? [];
  const hasSim = sim.length > 0;
  const hasLive = live.length > 0;

  if (positionsQ.isPending) {
    return (
      <div className="card">
        <h2>Positions</h2>
        <div className="muted small">loading…</div>
      </div>
    );
  }
  if (positionsQ.isError) {
    return (
      <div className="card">
        <h2>Positions</h2>
        <div className="muted small">load failed — {(positionsQ.error as Error)?.message?.slice(0, 80) ?? "error"}</div>
      </div>
    );
  }
  if (!hasSim && !hasLive) {
    return (
      <div className="card">
        <h2>Positions</h2>
        <div className="muted small">no open positions</div>
      </div>
    );
  }

  // Group live rows by account for per-account sub-headers (mirrors the
  // old real-section structure; the endpoint tags source per row).
  const liveByAccount = new Map<number, PositionRow[]>();
  for (const r of live) {
    const list = liveByAccount.get(r.account_id) ?? [];
    list.push(r);
    liveByAccount.set(r.account_id, list);
  }

  return (
    <div className="card">
      <h2>Positions</h2>
      <div className="muted small" style={{ marginTop: -6, marginBottom: 6 }}>
        {hasLive ? `${live.length} live · ` : ""}
        {hasSim ? `${sim.length} sim` : ""}
      </div>

      {hasLive && (
        <div style={{ marginBottom: hasSim ? 10 : 0 }}>
          {[...liveByAccount.entries()].map(([accountId, rows], i) => (
            <div key={accountId} style={{ marginBottom: i < liveByAccount.size - 1 ? 8 : 0 }}>
              <div className="regime-label muted small" style={{ marginBottom: 3 }}>
                {rows[0]!.account_name}
              </div>
              <LiveTable rows={rows} />
            </div>
          ))}
        </div>
      )}

      {hasSim && (
        <div>
          <div className="regime-label muted small" style={{ marginBottom: 3 }}>
            Sim
          </div>
          <SimTable rows={sim} />
        </div>
      )}
    </div>
  );
}
